<script lang="ts">
	import Button from '$lib/buttons/v3/button.svelte';
	import Plus from '$lib/icons/plus.svelte';
	import Overlay from '$lib/storyblok/components/overlay.svelte';
	import type { ImageBlock } from '../imageBlock.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import { fade, slide } from 'svelte/transition';
	import MinusV2 from '$lib/icons/minusV2.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';

	interface Props {
		content: ISbStoryData<{
			image: Array<ISbStoryData<ImageBlock>['content']>;
			card_bg: string;
			card_text: string;
			expanded_text: ISbRichtext;
			modal: Array<ISbStoryData['content']>;
			is_expandable?: boolean;
		}>['content'];
	}

	let { content }: Props = $props();
	let expanded = $state(false);

	let overlay = $state<{ show: () => void }>();

	let image = $derived(content.image?.[0]);

	$effect(() => {
		if (image) {
			image.rounded_corners = 'none';
		}
	});

	let modal = $derived(content.modal?.[0]);

	// TODO: clean up and remove modal variant
</script>

<article class="relative h-full" use:editable={content}>
	{#if content.image.length > 0}
		<div class="h-full w-full select-none">
			<Blok content={content.image[0]} />
		</div>
	{/if}
	<!-- svelte-ignore a11y_click_events_have_key_events -->
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div
		class="absolute bottom-0 right-0 m-4 flex max-h-[calc(100%-32px)] flex-col gap-y-6 overflow-hidden rounded-xl text-white max-lg:left-0 lg:m-8 lg:max-h-[calc(100%-64px)] lg:w-[400px]"
		class:cursor-pointer={content.is_expandable && content.expanded_text}
		style="background-color: {content.card_bg}"
		onclick={() => {
			if (!content.is_expandable) return;

			expanded = !expanded;
		}}
	>
		<div class="scrollbar-hide relative overflow-y-scroll">
			<div class={['px-7 pt-6', { 'pb-6': !content.is_expandable }]}>
				<h1
					class="mb-3 text-[24px]/[108%] tracking-[-.24px] lg:text-[32px]/[110%] lg:tracking-[-.32px]"
				>
					{content.card_text}
				</h1>

				{#if expanded || !content.is_expandable}
					<div transition:slide={{ duration: 300 }}>
						<div class="expanded-text" transition:fade={{ duration: 400 }}>
							<SbRichtext text={content.expanded_text} />
						</div>
					</div>
				{/if}

				{#if content.expanded_text && content.is_expandable}
					<div class="sticky bottom-0 left-0 z-20 pb-6 pt-3">
						{#if expanded}
							<p in:fade={{ duration: 400 }} class="flex items-center gap-x-2 opacity-60">
								<span>Close</span>
								<MinusV2 class="-mb-[2px] h-4 w-4" />
							</p>
						{:else}
							<p in:fade={{ duration: 400 }} class="flex items-center gap-x-2">
								<span>Learn more</span>
								<Plus class="-mb-[1px] h-4 w-4" />
							</p>
						{/if}
					</div>
				{/if}

				{#if modal && !content.expanded_text}
					<div class="-ml-6 mb-2">
						<Button onclick={() => overlay?.show()} class="btn-tertiary btn-brown"
							><span>Learn more</span>
							<Plus class="h-6 w-6" />
						</Button>
					</div>
				{/if}
			</div>
		</div>

		{#if content.is_expandable && content.expanded_text}
			<div
				class="linear-bg absolute bottom-0 h-15 w-full"
				style="--bg-color: {content.card_bg}"
			></div>
		{/if}
	</div>
</article>

{#if modal && !content.expanded_text}
	<Overlay bind:this={overlay}>
		<Blok content={modal} />
	</Overlay>
{/if}

<style>
	.linear-bg {
		background: linear-gradient(to bottom, transparent 0%, var(--bg-color) 40%);
	}

	div.expanded-text {
		:global(a) {
			text-decoration: underline;
		}
	}
</style>
